import {
  CSSProperties,
  Suspense,
  startTransition,
  useMemo,
  useTransition,
} from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "design_system_radar/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design_system_radar/dialog";
import {
  ArrowIcon,
  ChartAltIcon,
  ChartIcon,
  CheckAltIcon,
  DownloadCloudIcon,
  ExitIcon,
  EyesIcon,
  RadarLogo,
  SquashedRadarLogo,
  UploadIcon,
} from "design_system_radar/icons";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  useSidebar,
} from "design_system_radar/sidebar";
import { Toaster } from "design_system_radar/sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "design_system_radar/tooltip";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useNavigation,
} from "react-router";

import { ENV, isDevEnvironment } from "@/config/environment";
import { NavigationTransitionContext } from "@/config/routing/NavigationTransitionContext";
import { useDownloadNotificationStore } from "@/hooks/useDownloadNotificationStore";
import { usePendingReviewUploads } from "@/hooks/usePendingReviewUploads";
import { usePreviewExitGuardStore } from "@/hooks/usePreviewExitGuardStore";
import { useReadOnlyUser } from "@/modules/auth/useReadOnlyUser";
import ReconcilliationServiceProvider from "@/provider/ReconciliationService.tsx";

import { routes } from "../../config/routing/routes";
import useAuth from "../../modules/auth/useAuth";
import { fetchViews } from "../../services/views";
import ReconciliationWorkingV2 from "../admin/ReconciliationWorkingV2.tsx";
import { ConciliarButton } from "../ConciliarButton.tsx";
import FullScreenLoader from "../FullScreenLoader.tsx";
import { PendingReviewIndicator } from "./PendingReviewIndicator";

/**
 * Hook to determine which menu item is currently active based on the current URL
 * @returns The index of the active menu item, or -1 if none is active
 *
 * To add new active routes:
 * 1. Update the items array with the correct URL
 * 2. The function will automatically detect when the current path matches the item URL
 * 3. For more complex matching (e.g., partial matches), extend the logic here
 */
const useActiveMenuIndex = (items: typeof BASE_ITEMS) => {
  const location = useLocation();
  const currentPath = location.pathname;

  return items.findIndex((item) => {
    // Skip items with "#" URLs (not implemented yet)
    if (item.url === "#") {
      return false;
    }

    // Check if current path matches the item URL exactly
    return currentPath === item.url;
  });
};

// Base menu items — Vistas is injected dynamically based on tenant config.
export const BASE_ITEMS = [
  {
    title: "Aprobaciones",
    url: routes.approvals_v2.absolute,
    icon: CheckAltIcon,
  },
  {
    title: "Grupos",
    url: routes.movement_v2.absolute,
    icon: ChartAltIcon,
  },
  {
    title: "Fuentes",
    url: routes.sources_v2.absolute,
    icon: ChartIcon,
  },
  {
    title: "Datos",
    url: routes.data_v2.absolute,
    icon: UploadIcon,
  },
  {
    title: "Descargas",
    url: routes.downloadCenter.absolute,
    icon: DownloadCloudIcon,
  },
];

export const NavigationPendingOutlet = () => {
  const location = useLocation();
  const navigation = useNavigation();
  const [isTransitionPending, startNavigationTransition] = useTransition();
  const isNavigating =
    isTransitionPending ||
    (navigation.state !== "idle" &&
      navigation.location?.pathname !== location.pathname);

  return (
    <NavigationTransitionContext.Provider value={startNavigationTransition}>
      <div
        aria-busy={isNavigating}
        className="relative flex min-h-0 flex-1 flex-col"
      >
        <div className="flex min-h-0 flex-1 flex-col" inert={isNavigating}>
          <Suspense fallback={<FullScreenLoader />}>
            <Outlet />
          </Suspense>
        </div>
        {isNavigating && (
          <div
            aria-label="Cargando página"
            aria-live="polite"
            className="absolute inset-0 z-50 bg-[#171821]"
            role="status"
          >
            <FullScreenLoader />
          </div>
        )}
      </div>
    </NavigationTransitionContext.Provider>
  );
};

const AdminLayoutV2 = () => {
  return (
    <ReconcilliationServiceProvider>
      <ReconciliationWorkingV2 />
      <SidebarProvider
        className="h-svh overflow-hidden"
        data-radar-environment={ENV}
        style={
          isDevEnvironment
            ? ({
                "--sidebar-accent-foreground": "hsl(0 0% 0%)",
              } as CSSProperties)
            : undefined
        }
      >
        <SidebarWithOpen />
        <SidebarInset className="overflow-hidden">
          <div className="h-full min-h-0 flex flex-col mx-auto p-6 w-full bg-[#171821] overflow-hidden">
            <Toaster
              closeButton
              toastOptions={{
                classNames: {
                  closeButton:
                    "!left-auto !right-0 !translate-x-[35%] !-translate-y-[35%]",
                },
              }}
            />
            <NavigationPendingOutlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </ReconcilliationServiceProvider>
  );
};

export default AdminLayoutV2;

export const buildSidebarItems = ({
  hasViews,
  isReadOnlyUser,
}: {
  hasViews: boolean;
  isReadOnlyUser: boolean;
}) => {
  const baseItems = isReadOnlyUser
    ? BASE_ITEMS.filter((item) => item.title !== "Datos")
    : BASE_ITEMS;

  if (!hasViews) {
    return baseItems;
  }

  const next = [...baseItems];
  next.splice(3, 0, {
    title: "Vistas",
    url: routes.views_v2.absolute,
    icon: EyesIcon,
  });
  return next;
};

const SidebarWithOpen = () => {
  const { open, toggleSidebar } = useSidebar();
  const { logout } = useAuth();
  const isReadOnlyUser = useReadOnlyUser();
  const hasPendingDownload = useDownloadNotificationStore(
    (state) => state.hasPendingDownload,
  );
  // Read-only users can't review uploads and don't see the "Datos" item, so
  // skip the poll entirely for them.
  const { count: pendingReviewCount } = usePendingReviewUploads({
    enabled: !isReadOnlyUser,
  });

  const { data: viewsData } = useQuery({
    queryKey: ["views"],
    queryFn: () => fetchViews(),
    retry: 3,
  });

  const items = useMemo(() => {
    return buildSidebarItems({
      hasViews: (viewsData?.data.views.length ?? 0) > 0,
      isReadOnlyUser,
    });
  }, [isReadOnlyUser, viewsData]);

  const activeIndex = useActiveMenuIndex(items);
  const client = useQueryClient();
  const navigate = useNavigate();
  const onSameRouteExit = usePreviewExitGuardStore(
    (state) => state.onSameRouteExit,
  );

  const handleLogout = () => {
    if (logout) {
      logout();
      return;
    }

    client.invalidateQueries();
    startTransition(() => {
      navigate(routes.root.path);
    });
  };

  return (
    <Sidebar className="w-48" collapsible="icon">
      <SidebarHeader className="flex items-center justify-center py-7">
        {open ? <RadarLogo size={104} /> : <SquashedRadarLogo size={42} />}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem className="mb-18 flex items-center justify-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="inline-flex w-fit">
                      <ConciliarButton expanded={open} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="center" hidden={open}>
                    Conciliar
                  </TooltipContent>
                </Tooltip>
              </SidebarMenuItem>
              {items.map((item, index) => {
                const isDownloads = item.url === routes.downloadCenter.absolute;
                const isData = item.url === routes.data_v2.absolute;
                return (
                  <SidebarMenuItem key={item.title}>
                    <div className="relative rounded-md">
                      <SidebarMenuButton
                        asChild
                        isActive={activeIndex === index}
                        tooltip={item.title}
                      >
                        <Link
                          to={item.url}
                          onClick={(event) => {
                            // Clicking the already-active "Datos" item while an
                            // upload preview is pending fires no route change,
                            // so the route blocker can't catch it; intercept it
                            // here to prompt the same exit modal.
                            if (isData && onSameRouteExit) {
                              event.preventDefault();
                              onSameRouteExit();
                              return;
                            }
                            if (isDownloads && hasPendingDownload) {
                              useDownloadNotificationStore
                                .getState()
                                .setPendingDownload(false);
                            }
                          }}
                        >
                          <item.icon
                            size={20}
                            className="text-radar-120 transition-colors"
                          />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                      {isData && (
                        <PendingReviewIndicator
                          count={pendingReviewCount}
                          expanded={open}
                        />
                      )}
                    </div>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="relative">
        <Button
          variant="secondary"
          className="absolute -top-2.5 -right-3 size-6 rounded-full p-0.5"
          onClick={toggleSidebar}
        >
          <ArrowIcon
            size={20}
            className={`transition-all duration-100 ${
              open ? "rotate-0" : "rotate-180"
            }`}
          />
        </Button>
        <SidebarMenu>
          <SidebarMenuItem>
            <Dialog>
              <DialogTrigger asChild>
                <SidebarMenuButton tooltip="Cerrar sesión">
                  <ExitIcon />
                  <span>Cerrar sesión</span>
                </SidebarMenuButton>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cerrar sesión</DialogTitle>
                  <DialogDescription>
                    ¿Estás seguro de que quieres cerrar sesión? Si tienes
                    cambios no guardados, se perderán.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="ghost">Cancelar</Button>
                  </DialogClose>
                  <Button onClick={handleLogout}>Cerrar sesión</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
